import React, { useEffect, useState } from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { Checkbox } from 'formik-material-ui';
import * as Yup from 'yup';
import moment from 'moment';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import * as RewardTemplateService from '../../api/rewardTemplateService';
import * as RewardCardService from '../../api/rewardCardService';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';
import InlineTextField from '../FormikAdapter/InlineTextField';
import DatePicker from '../FormikAdapter/DatePicker';

const useStyles = makeStyles((theme) => ({
  content: {
    [theme.breakpoints.up('sm')]: {
      width: 500,
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  header: {
    marginTop: theme.spacing(2),
  },
  actionContainer: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  box: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  titleContainer: {
    marginRight: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rewardTemplateContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  expiryInput: {
    width: 40,
  },
  expiryDate: {
    marginLeft: theme.spacing(),
    width: 90,
  },
}));

export interface Values {
  rewardTemplateIdList: string[];
  validityPeriodDays: number;
  expiresAt: moment.Moment | null;
}

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  onSuccess: () => void;
  memberId: string;
};

const useRewardTitleStyles = makeStyles((theme) => ({
  root: ({ isSelected }: { isSelected: boolean }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(),
    borderRadius: 5,
    borderWidth: isSelected ? 2 : 1,
    borderStyle: 'solid',
    borderColor: isSelected
      ? theme.palette.primary.main
      : theme.palette.grey[300],
    width: 300,
    height: 80,
    marginLeft: theme.spacing(2),
  }),
}));

export function RewardTitle({
  rewardId,
  rewardTemplateIdList,
  title,
  handleClick,
}: {
  rewardId: string;
  rewardTemplateIdList: string[];
  title: string;
  handleClick: (updateList: string[]) => void;
}) {
  const isSelected = rewardTemplateIdList.includes(rewardId);
  const classes = useRewardTitleStyles({ isSelected });
  return (
    <div
      className={classes.root}
      onClick={() => {
        if (isSelected) {
          handleClick(rewardTemplateIdList.filter((id) => id !== rewardId));
        } else {
          handleClick(rewardTemplateIdList.concat([rewardId]));
        }
      }}
    >
      <Typography variant={isSelected ? 'h6' : 'body2'}>{title}</Typography>
    </div>
  );
}

export default function AddRewardDialog({
  open,
  onClose,
  onSuccess,
  memberId,
}: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();
  const [rewardTemplateList, setRewardTemplateList] = useState<
    RewardTemplateService.RewardTemplate[] | null
  >(null);
  const [isDefaultChange, setIsDefaultChange] = useState(false);
  useEffect(() => {
    const fetchRewardTemplate = async () => {
      if (company) {
        const res = await RewardTemplateService.list(company.id);
        setRewardTemplateList(res);
      }
    };
    fetchRewardTemplate();
  }, [company]);

  const initialValues: Values = {
    rewardTemplateIdList: [],
    validityPeriodDays: 60,
    expiresAt: null,
  };

  const ValidationSchema = Yup.object().shape({
    rewardTemplateIdList: Yup.array().min(1, 'Please select a reward template'),
  });

  const handleSubmit = async (values: Values, _: FormikHelpers<Values>) => {
    if (company && memberId) {
      try {
        if (values.expiresAt) {
          await RewardCardService.issue({
            companyId: company.id,
            memberID: memberId,
            expiryDate: values.expiresAt.unix(),
            rewardTemplateIDs: values.rewardTemplateIdList,
          });
        }
        if (values.validityPeriodDays && values.validityPeriodDays > 0) {
          await RewardCardService.issue({
            companyId: company.id,
            memberID: memberId,
            validityPeriodDays: parseInt(
              values.validityPeriodDays.toString(10),
              10
            ),
            rewardTemplateIDs: values.rewardTemplateIdList,
          });
        }
        onSuccess();
      } catch (e: any) {
        console.log('Error: ', e);
      }
    }
  };

  // TODO - Display a loader
  if (!company) {
    return null;
  }

  if (!rewardTemplateList) {
    return null;
  }

  return (
    <FullScreenDialog open={open} keepMounted>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({
          isSubmitting,
          setFieldValue,
          values: { rewardTemplateIdList },
        }) => (
          <Form noValidate>
            <div className={classes.box}>
              <IconButton onClick={onClose}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            </div>
            <Grid container>
              <Grid item xs={12}>
                <div className={classes.content}>
                  <Container maxWidth="md">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <div className={classes.titleContainer}>
                          <Typography variant="h4">Add Rewards</Typography>
                        </div>
                      </Grid>

                      <Grid item xs={12}>
                        {rewardTemplateList.map((rewardTemplate, index) => (
                          <div
                            className={classes.rewardTemplateContainer}
                            key={index}
                          >
                            <Field
                              id={rewardTemplate.id}
                              component={Checkbox}
                              type="checkbox"
                              name="rewardTemplateIdList"
                              value={rewardTemplate.id}
                            />
                            <RewardTitle
                              rewardId={rewardTemplate.id}
                              rewardTemplateIdList={rewardTemplateIdList}
                              title={rewardTemplate.title}
                              handleClick={(newList: string[]) => {
                                setFieldValue('rewardTemplateIdList', newList);
                              }}
                            />
                          </div>
                        ))}
                      </Grid>
                      <Grid item xs={12}>
                        {!isDefaultChange && (
                          <div className={classes.titleContainer}>
                            <Typography variant="body1">
                              Reward expires in 60 days by default.
                            </Typography>
                            <Button
                              variant="text"
                              color="primary"
                              onClick={() => {
                                setIsDefaultChange(true);
                              }}
                            >
                              Change
                            </Button>
                          </div>
                        )}
                        {isDefaultChange && (
                          <div className={classes.titleContainer}>
                            <Field
                              component={InlineTextField}
                              name="validityPeriodDays"
                              label="Expires in"
                              labelRight={'days, or on '}
                              className={classes.expiryInput}
                              onFocus={() => {
                                setFieldValue('expiresAt', null);
                              }}
                            />
                            <Field
                              component={DatePicker}
                              name="expiresAt"
                              className={classes.expiryDate}
                              format="D MMM YYYY"
                              onFocus={() => {
                                setFieldValue('validityPeriodDays', 0);
                              }}
                              disablePast
                            />
                          </div>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <div className={classes.actionContainer}>
                          <Button onClick={() => onClose()}>Cancel</Button>
                          <Button
                            type="submit"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            Add Reward
                          </Button>
                        </div>
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </FullScreenDialog>
  );
}
